// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

;globalThis["_sentryNextJsVersion"] = "16.3.8";import * as Sentry from "@sentry/nextjs";
import { scrubEvent } from "@/lib/monitoring/scrub-pii";

const isProd = process.env.NODE_ENV === "production";

/**
 * Never report from a developer's own machine.
 *
 * On 2026-08-12 a local run started with NODE_ENV=production, for testing a
 * consent banner, put two errors into the PRODUCTION Sentry project tagged
 * `environment: production`, from `http://localhost:3444/`. Tom saw them and
 * reasonably assumed the live app was broken.
 *
 * That is worse than untidy. Error monitoring is only useful if everything in it
 * is real; a stream of localhost noise is how a genuine error gets scrolled past.
 *
 * Filtering on the URL host rather than on NODE_ENV or a Vercel-only variable is
 * deliberate: it cannot accidentally switch off reporting for the real deployed
 * app, which is the failure that would actually cost something.
 */
function isLocalhostEvent(event: Sentry.ErrorEvent): boolean {
  const candidates = [
    event.request?.url,
    typeof window === "undefined" ? undefined : window.location?.href,
  ].filter(Boolean) as string[];
  return candidates.some((u) => /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:|\/|$)/i.test(u));
}

Sentry.init({
  dsn: "https://979f8319fa738ef95a4b71dc50103ff4@o4511095672799232.ingest.de.sentry.io/4511095678369872",

  environment: process.env.NODE_ENV ?? "development",

  // Session Replay is deliberately NOT enabled.
  //
  // replayIntegration() bundles rrweb (~556 KB raw — the single largest chunk we shipped) and
  // attaches a full-DOM MutationObserver to the main thread for 100% of users, in order to record
  // 5% of them. On a swipe-driven UI that recorder is a direct cause of scroll/swipe/typing jank.
  // PostHog was independently running a second DOM recorder at the same time (see lib/posthog.ts).
  //
  // Error, tracing and log capture are unaffected by this — only replay video is gone. If replay is
  // wanted again, load it off the critical path with Sentry.lazyLoadIntegration("replayIntegration")
  // and make sure only ONE recorder is ever active.
  //
  // Deliberately NOT `integrations: []`. That line was here until 2026-08-23 and it did far more
  // than drop replay: replayIntegration() is not a default, it has to be added by hand, so an empty
  // array bought nothing and instead REPLACED the whole default set. The one that mattered is
  // globalHandlers, which is what listens on window.onerror and unhandledrejection. Without it
  // Sentry only ever recorded explicit captureException() calls, so every uncaught error and every
  // unhandled rejection in the browser went unreported: the project logged 3 issues in 90 days
  // while the phone app was visibly broken. Leaving this key off keeps the defaults, and replay
  // still does not load.

  // Sample 20% of traces in production; 100% in dev for full visibility.
  tracesSampleRate: isProd ? 0.2 : 1.0,

  // Off: application logs were shipping members' email addresses and phone numbers to Sentry,
  // which is not named in the privacy policy. Errors and traces still arrive; log lines do not.
  enableLogs: false,

  // Off. This used to be on, which attached members' email addresses, IP addresses, request
  // headers and cookies to every event. A user is still identified by id via Sentry.setUser(),
  // which is the part that is actually useful when debugging.
  sendDefaultPii: false,

  beforeSend(event) {
    if (isLocalhostEvent(event)) return null;
    const msg = event.exception?.values?.[0]?.value ?? "";

    // Drop network-level and browser-cancel errors — not actionable
    if (
      /Failed to fetch|NetworkError|Load failed|The Internet connection appears to be offline/i.test(msg) ||
      /AbortError|signal is aborted|Request aborted/i.test(msg) ||
      // Next.js router navigation aborts its own in-flight fetches — not a real error
      /Invariant: attempted to hard navigate|NEXT_REDIRECT|NEXT_NOT_FOUND/i.test(msg) ||
      // React finding the DOM changed underneath it. In practice this is a browser
      // extension, a password manager or Google Translate rewriting the page, not us,
      // and there is nothing to fix on our side. Added 2026-09-17 after GROWTHSYNC-S
      // (insertBefore on /auth/signup) reached Slack, where crashes now go: an alert
      // nobody can act on is the noise that teaches you to skim past the real ones.
      /Failed to execute '(insertBefore|removeChild)' on 'Node'/i.test(msg)
    ) {
      return null;
    }

    return scrubEvent(event);
  },
  // A visitor's wallet or security extension (MetaMask and others running SES "lockdown")
  // freezes Array.prototype, and Turbopack's own chunk loader then throws on
  // `globalThis.TURBOPACK.push`. Not our code and not fixable here; normal browsers are clean.
  //
  // Second entry, GROWTHSYNC-G (2026-09-28): a script served from about:blank, so injected by an
  // extension or a crawler's automation, replaces EventTarget.prototype.addEventListener with a
  // wrapper (`generalAddEventListenerLogic`) that keeps listeners in `elm.events`. When Sentry's own
  // browserTracing registers its interaction listener, that wrapper throws on its own bookkeeping.
  // No GrowthSync code is in the stack, 0 users were attached, and nothing on our side can fix it.
  // Exact message only, so a genuine `.push is not a function` bug elsewhere still reports.
  ignoreErrors: [
    /Cannot assign to read only property 'push' of object '\[object Array\]'/,
    /^elm\.events\.push is not a function$/,
  ],
});

/* public/scripts/global-error-handler.js runs beforeInteractive, long before this module, and it
   owns window.onerror. Sentry's globalHandlers integration wraps that handler rather than replacing
   it, so restoring the default integrations above is what actually gets those errors reported. This
   line is the backstop: @sentry/nextjs does not put itself on window, so without it the explicit
   captureException() in that script can never fire. dedupeIntegration is a default, so the two
   paths reporting the same error do not produce two issues. */
if (typeof window !== "undefined") {
  (window as unknown as { Sentry?: typeof Sentry }).Sentry = Sentry;
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
